import {
  RouteProp,
  StackActions,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useRef } from 'react';
import ActivityIndicatorScreen from '../../../components/ActivityIndicatorScreen';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { QualificationParamList } from '../../../web-navigation/QualificationNavigator';
import { useGetStudyIntake } from '../../../hooks/study/useGetStudyIntake';
import AlertBox from '../../../components/AlertBox';
import { useQualifyStudyParticipant } from '../../../hooks/study-participant/useQualifyStudyParticipant';

type QualificationProps<T> = FormElementProps<T, QualificationConfig>;

export const Qualification = <T extends object>({
  element,
  stateNode
}: QualificationProps<T>) => {
  const { emitEvent } = useDynamicFormContext<any, any>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route =
    useRoute<RouteProp<QualificationParamList, 'QualificationForm'>>();
  const studyParticipantId = route.params?.studyParticipantId;
  const { qualifyStudyParticipant, qualifiedStudyParticipant } =
    useQualifyStudyParticipant(studyParticipantId || -1);
  const { qualified, redirectPath, redirectFormTemplateId, html } =
    element.config || {};

  const { loadingStudyIntakeError, getStudyIntake } = useGetStudyIntake();

  // Capture stateNode.type at the moment we kick off the qualification.
  // The qualification effect and the post-success handler are split across
  // two effects (this one fires qualifyStudyParticipant; the next one
  // emits 'final' once qualifiedStudyParticipant resolves), and during
  // the network round-trip the state machine can transition away from
  // 'final'. Without this capture, a post-await stateNode?.type check
  // would return false and 'final' would silently never emit — leaving
  // the form stuck.
  const wasFinalAtFireRef = useRef<boolean>(false);

  useEffect(() => {
    if (studyParticipantId) {
      // Read stateNode?.type at the moment qualification is kicked off
      // and stash it on the ref so the second effect can use it after
      // the network round-trip. Intentionally NOT in the dep array —
      // adding it would re-fire qualifyStudyParticipant whenever the
      // state machine transitions while mounted, causing duplicate
      // network calls.
      wasFinalAtFireRef.current = stateNode?.type === 'final';
      qualifyStudyParticipant(qualified == 'Yes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyParticipantId, qualified, qualifyStudyParticipant, redirectPath]);

  useEffect(() => {
    if (qualifiedStudyParticipant) {
      const { study } = qualifiedStudyParticipant;
      const handleQualification = async () => {
        if (wasFinalAtFireRef.current) {
          await emitEvent('final', { transitions: [] });
        }
        if (
          route.params.fromJoinScreen &&
          qualifiedStudyParticipant.qualified &&
          study?.qualificationFormTemplateId
        ) {
          if (study.isPublic) {
            const studyIntake = await getStudyIntake(
              qualifiedStudyParticipant.id
            );
            if (!studyIntake) return;
            navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'StudiesNav',
                  state: {
                    index: 1,
                    routes: [
                      { name: 'MyStudies' },
                      {
                        name: 'FormTemplate',
                        params: {
                          formTemplateId: studyIntake.formTemplateId,
                          entityDataId: studyIntake.id,
                          studyParticipantId: qualifiedStudyParticipant.id,
                          message: html
                        }
                      }
                    ]
                  }
                }
              ]
            });
          } else if (!study.isPublic) {
            navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'StudiesNav',
                  state: {
                    index: 1,
                    routes: [
                      { name: 'MyStudies' },
                      {
                        name: 'QualificationComplete',
                        params: {
                          displayNotQualified: false,
                          message: html,
                          fromJoinScreen: route.params.fromJoinScreen
                        }
                      }
                    ]
                  }
                }
              ]
            });
          }
        } else if (
          route.params.fromJoinScreen &&
          !qualifiedStudyParticipant.qualified
        ) {
          navigation.reset({
            index: 0,
            routes: [
              {
                name: 'StudiesNav',
                state: {
                  index: 1,
                  routes: [
                    { name: 'MyStudies' },
                    {
                      name: 'QualificationComplete',
                      params: {
                        displayNotQualified: true,
                        message: html,
                        fromJoinScreen: route.params.fromJoinScreen
                      }
                    }
                  ]
                }
              }
            ]
          });
        } else {
          navigation.dispatch(
            StackActions.replace('Qualification', {
              screen: 'QualificationComplete',
              params: {
                studyParticipantId,
                message: html
              }
            })
          );
        }
        return;
      };
      // wait till next render loop since StudyEventFormDataSource doesn't
      // register the 'final' subscribe immediately
      setTimeout(() => handleQualification());
    }
  }, [
    // stateNode intentionally not in deps — this effect reads
    // wasFinalAtFireRef.current (set in the first effect) rather than
    // stateNode?.type, so re-running on state-machine transitions
    // would be wasted work.
    navigation,
    redirectPath,
    redirectFormTemplateId,
    emitEvent,
    studyParticipantId,
    route.params.fromJoinScreen,
    qualified,
    getStudyIntake,
    route,
    qualifiedStudyParticipant
  ]);

  return loadingStudyIntakeError ? (
    <AlertBox severity='error'>{loadingStudyIntakeError.message}</AlertBox>
  ) : (
    <ActivityIndicatorScreen backgroundColor='transparent' />
  );
};

interface QualificationConfig {
  qualified?: string;
  redirectPath?: string;
  redirectFormTemplateId?: number;
  html?: string;
}

export const QualificationPlugin: FormElementPlugin<any, QualificationConfig> =
  {
    name: 'Qualification',
    type: 'Qualification',
    element: Qualification as React.FC<
      FormElementProps<any, QualificationConfig>
    >
  };
